// ─── Public Status Page — measure a box, in px ───────────────────────
//
// The history bar is the one part of this page that CANNOT be laid out by CSS
// alone: it renders one cell per day, and the number of days that fit is a
// function of the container's pixel width. 90 cells fit a laptop and do not fit
// a phone, so HistoryGrid measures its own track and drops days from the OLDEST
// end until the rest fit — "today" must stay flush right at every width.
//
// ResizeObserver rather than a window resize listener: the bar also narrows when
// a sibling grows (a long component name, a wrapped status pill), which a
// window listener never sees. A window resize fallback covers engines without
// ResizeObserver, where the page degrades to a correct-but-less-reactive bar.

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Returns a ref to attach to the element, and its current CONTENT width in CSS
 * pixels (0 before the first measurement — render nothing width-dependent until
 * it is non-zero).
 */
export function useElementWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  const measure = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    // getBoundingClientRect includes padding and border; the callers use
    // border-box sizing with no horizontal padding on the track, so this is the
    // usable width. Round DOWN so a fractional pixel never overflows the row.
    const next = Math.floor(node.getBoundingClientRect().width);
    setWidth((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(() => measure());
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure]);

  return [ref, width];
}
